import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import {
  Gear,
  Database,
  EnvelopeSimple,
  Key,
  Bell,
  Shield,
  Globe,
  Palette,
  CloudArrowUp,
  CheckCircle,
  FloppyDisk,
  ArrowsClockwise,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import { typography, components, spacing } from '../../styles/design-system'

export default function SettingsPage() {
  const { user } = useAuth()
  const { t } = useLanguage()
  const { showSuccess, confirm } = useDialog()
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState('general')

  // TODO: Replace with actual settings data from Supabase
  const [settings, setSettings] = useState({
    // General Settings
    platformName: 'Eximtech',
    platformUrl: 'https://eximtech.com.br',
    supportEmail: 'suporte@eximtech.com.br',
    defaultLanguage: 'pt-BR',
    timezone: 'America/Sao_Paulo',

    // Email Settings
    emailProvider: 'brevo',
    emailHost: 'smtp-relay.brevo.com',
    emailPort: '587',
    emailFrom: 'noreply@eximtech.com.br',

    // Security Settings
    requireEmailVerification: true,
    allowPasswordReset: true,
    sessionTimeout: '7200',
    maxLoginAttempts: '5',
    enableTwoFactor: false,

    // Notifications
    notifyOnNewTenant: true,
    notifyOnPaymentFailed: true,
    notifyOnSystemError: true,
    dailySummaryEmail: true,

    // API Settings
    rateLimit: '1000',
    apiVersion: 'v1',
    enableWebhooks: true,

    // Storage
    storageProvider: 'supabase',
    maxFileSize: '10',
    allowedFileTypes: 'pdf,jpg,png,xlsx,docx',
  })

  const tabs = [
    { id: 'general', label: 'Geral', icon: Gear },
    { id: 'email', label: 'Email', icon: EnvelopeSimple },
    { id: 'security', label: 'Segurança', icon: Shield },
    { id: 'notifications', label: 'Notificações', icon: Bell },
    { id: 'api', label: 'API', icon: Key },
    { id: 'storage', label: 'Armazenamento', icon: Database },
  ]

  const handleSave = () => {
    // TODO: Implement save settings to Supabase
    console.log('Saving settings:', settings)
    showSuccess('Sucesso', 'Configurações salvas com sucesso!')
  }

  const handleReset = async () => {
    // TODO: Implement reset to defaults
    const confirmed = await confirm({ title: 'Restaurar Padrões', message: 'Deseja restaurar as configurações padrão?' })
    if (confirmed) {
      console.log('Resetting to defaults')
    }
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className={`${spacing.containerMaxWidth} ${spacing.containerPadding}`}>
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className={typography.pageTitle}>Configurações do Sistema</h1>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="default"
                onClick={handleReset}
                className="gap-2"
              >
                <ArrowsClockwise size={18} />
                Restaurar Padrões
              </Button>
              <Button
                size="default"
                onClick={handleSave}
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <FloppyDisk size={18} weight="bold" />
                Salvar Alterações
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`${spacing.containerMaxWidth} ${spacing.containerPadding} py-6`}>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className={components.card}>
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                        activeTab === tab.id
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon
                        size={20}
                        className={activeTab === tab.id ? 'text-emerald-600' : 'text-gray-500'}
                      />
                      {tab.label}
                    </button>
                  )
                })}
              </nav>
            </div>
          </div>

          {/* Settings Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* General Settings */}
            {activeTab === 'general' && (
              <div className={components.card}>
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h2 className={typography.sectionTitle}>Configurações Gerais</h2>
                  <p className={typography.bodySecondary}>Configurações básicas da plataforma</p>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome da Plataforma
                    </label>
                    <Input
                      type="text"
                      value={settings.platformName}
                      onChange={(e) =>
                        setSettings({ ...settings, platformName: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      URL da Plataforma
                    </label>
                    <Input
                      type="text"
                      value={settings.platformUrl}
                      onChange={(e) =>
                        setSettings({ ...settings, platformUrl: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email de Suporte
                    </label>
                    <Input
                      type="email"
                      value={settings.supportEmail}
                      onChange={(e) =>
                        setSettings({ ...settings, supportEmail: e.target.value })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Idioma Padrão
                      </label>
                      <select
                        value={settings.defaultLanguage}
                        onChange={(e) =>
                          setSettings({ ...settings, defaultLanguage: e.target.value })
                        }
                        className={components.filterInput}
                      >
                        <option value="pt-BR">Português (Brasil)</option>
                        <option value="en-US">English (US)</option>
                        <option value="es-ES">Español</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Timezone
                      </label>
                      <select
                        value={settings.timezone}
                        onChange={(e) =>
                          setSettings({ ...settings, timezone: e.target.value })
                        }
                        className={components.filterInput}
                      >
                        <option value="America/Sao_Paulo">São Paulo (GMT-3)</option>
                        <option value="America/New_York">New York (GMT-5)</option>
                        <option value="Europe/London">London (GMT+0)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Email Settings */}
            {activeTab === 'email' && (
              <div className={components.card}>
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h2 className={typography.sectionTitle}>Configurações de Email</h2>
                  <p className={typography.bodySecondary}>Configure o serviço de envio de emails (Brevo/Nodemailer)</p>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Provedor de Email
                    </label>
                    <select
                      value={settings.emailProvider}
                      onChange={(e) =>
                        setSettings({ ...settings, emailProvider: e.target.value })
                      }
                      className={components.filterInput}
                    >
                      <option value="brevo">Brevo (Sendinblue)</option>
                      <option value="sendgrid">SendGrid</option>
                      <option value="mailgun">Mailgun</option>
                      <option value="smtp">SMTP Customizado</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SMTP Host
                      </label>
                      <Input
                        type="text"
                        value={settings.emailHost}
                        onChange={(e) =>
                          setSettings({ ...settings, emailHost: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SMTP Port
                      </label>
                      <Input
                        type="text"
                        value={settings.emailPort}
                        onChange={(e) =>
                          setSettings({ ...settings, emailPort: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Remetente (From)
                    </label>
                    <Input
                      type="email"
                      value={settings.emailFrom}
                      onChange={(e) =>
                        setSettings({ ...settings, emailFrom: e.target.value })
                      }
                    />
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <div className="flex gap-3">
                      <CheckCircle size={20} className="text-emerald-600 flex-shrink-0" weight="fill" />
                      <div>
                        <p className="text-sm font-medium text-emerald-900">
                          Configurações do Brevo Ativas
                        </p>
                        <p className="text-sm text-emerald-700 mt-1">
                          As credenciais SMTP estão configuradas via variáveis de ambiente.
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" className="w-full gap-2">
                    <EnvelopeSimple size={16} />
                    Enviar Email de Teste
                  </Button>
                </div>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div className={components.card}>
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h2 className={typography.sectionTitle}>Configurações de Segurança</h2>
                  <p className={typography.bodySecondary}>Autenticação e controles de segurança</p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">
                        Verificação de Email Obrigatória
                      </p>
                      <p className="text-sm text-gray-500">
                        Usuários devem verificar email antes de acessar
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.requireEmailVerification}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          requireEmailVerification: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">
                        Permitir Recuperação de Senha
                      </p>
                      <p className="text-sm text-gray-500">
                        Usuários podem resetar senha via email
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.allowPasswordReset}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          allowPasswordReset: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">
                        Autenticação de Dois Fatores
                      </p>
                      <p className="text-sm text-gray-500">
                        Habilitar 2FA para todos os usuários master
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.enableTwoFactor}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          enableTwoFactor: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Timeout de Sessão (segundos)
                      </label>
                      <Input
                        type="number"
                        value={settings.sessionTimeout}
                        onChange={(e) =>
                          setSettings({ ...settings, sessionTimeout: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Máximo de Tentativas de Login
                      </label>
                      <Input
                        type="number"
                        value={settings.maxLoginAttempts}
                        onChange={(e) =>
                          setSettings({ ...settings, maxLoginAttempts: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Settings */}
            {activeTab === 'notifications' && (
              <div className={components.card}>
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h2 className={typography.sectionTitle}>Configurações de Notificações</h2>
                  <p className={typography.bodySecondary}>Configure alertas e notificações do sistema</p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Novo Tenant Criado</p>
                      <p className="text-sm text-gray-500">
                        Notificar admins quando um novo tenant é cadastrado
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.notifyOnNewTenant}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          notifyOnNewTenant: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Falha de Pagamento</p>
                      <p className="text-sm text-gray-500">
                        Alertar quando um pagamento falhar
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.notifyOnPaymentFailed}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          notifyOnPaymentFailed: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Erros do Sistema</p>
                      <p className="text-sm text-gray-500">
                        Notificar sobre erros críticos do sistema
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.notifyOnSystemError}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          notifyOnSystemError: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Resumo Diário</p>
                      <p className="text-sm text-gray-500">
                        Enviar email com resumo das atividades do dia
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.dailySummaryEmail}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          dailySummaryEmail: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* API Settings */}
            {activeTab === 'api' && (
              <div className={components.card}>
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h2 className={typography.sectionTitle}>Configurações de API</h2>
                  <p className={typography.bodySecondary}>Configure limites e webhooks da API</p>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Limite de Requisições (por hora)
                    </label>
                    <Input
                      type="number"
                      value={settings.rateLimit}
                      onChange={(e) =>
                        setSettings({ ...settings, rateLimit: e.target.value })
                      }
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Número máximo de requisições permitidas por tenant/hora
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Versão da API
                    </label>
                    <select
                      value={settings.apiVersion}
                      onChange={(e) =>
                        setSettings({ ...settings, apiVersion: e.target.value })
                      }
                      className={components.filterInput}
                    >
                      <option value="v1">v1 (Atual)</option>
                      <option value="v2">v2 (Beta)</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Habilitar Webhooks</p>
                      <p className="text-sm text-gray-500">
                        Permitir tenants configurarem webhooks
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.enableWebhooks}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          enableWebhooks: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Storage Settings */}
            {activeTab === 'storage' && (
              <div className={components.card}>
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h2 className={typography.sectionTitle}>Configurações de Armazenamento</h2>
                  <p className={typography.bodySecondary}>Gerencie storage e uploads de arquivos</p>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Provedor de Storage
                    </label>
                    <select
                      value={settings.storageProvider}
                      onChange={(e) =>
                        setSettings({ ...settings, storageProvider: e.target.value })
                      }
                      className={components.filterInput}
                    >
                      <option value="supabase">Supabase Storage</option>
                      <option value="aws">AWS S3</option>
                      <option value="gcs">Google Cloud Storage</option>
                      <option value="azure">Azure Blob Storage</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tamanho Máximo de Arquivo (MB)
                    </label>
                    <Input
                      type="number"
                      value={settings.maxFileSize}
                      onChange={(e) =>
                        setSettings({ ...settings, maxFileSize: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipos de Arquivo Permitidos
                    </label>
                    <Input
                      type="text"
                      value={settings.allowedFileTypes}
                      onChange={(e) =>
                        setSettings({ ...settings, allowedFileTypes: e.target.value })
                      }
                      placeholder="pdf,jpg,png,xlsx,docx"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Separado por vírgulas
                    </p>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-900 mb-3">
                      Uso de Storage
                    </p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Usado</span>
                        <span className="font-medium text-gray-900">24.5 GB / 100 GB</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 rounded-full bg-emerald-600"
                          style={{ width: '24.5%' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </AppLayout>
  )
}
